import { Box, Container, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { TFunction } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Router, withTranslation } from "../../../../i18n";
import ErrorPage from "../../../../src/components/ErrorPage";
import Footer from "../../../../src/components/Footer";
import ImageSlider from "../../../../src/components/ImageSlider";
import Loading from "../../../../src/components/Loading";
import Navbar from "../../../../src/components/NewNavbar";
import SelectCheckinCheckout from "../../../../src/components/SelectCheckinCheckout";
import { HotelDetailDto } from "../../../../src/libs/hotel";
import { getHotelById, getHotelImage } from "../../../../src/services/rooms";
import nextDayDate from "../../../../src/utils/nextDayDate";
import useStyles from "../../../../styles/hotels/select-date";

type Props = {
  readonly t: TFunction;
};

interface DateCheckInType {
  in: Date | null;
  out: Date | null;
}

interface ErrorDto {
  error_code: string;
  errors: string[];
  message: string;
  status_code: number;
}

const Rooms = (props: Props) => {
  const router = useRouter();
  const { hotel_username } = router.query;
  const classes = useStyles(props);
  const { t } = props;
  const [dateCheckin, setDateCheckin] = useState<DateCheckInType>({
    in: new Date(),
    out: nextDayDate(new Date()),
  });
  const [hotelDetail, setHotelDetail] = useState<HotelDetailDto>(Object);
  const [hotelImages, setHotelImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ErrorDto>(Object);

  const fetchHotelDetail = async () => {
    try {
      const res = await getHotelById(hotel_username);
      setHotelDetail(res.data);
      setIsLoading(false);
    } catch (error) {
      setError(error?.response?.data?.meta);
      setIsLoading(false);
    }
  };

  const fetchHotelImage = async (hotelId: string) => {
    try {
      const res = await getHotelImage(hotelId);
      const imageObject = res.data[0].images;
      const listImages = imageObject.map((object) => object.imageUrl);
      setHotelImages(listImages);
      setIsLoading(false);
    } catch (error) {
      setError(error?.response?.data?.meta);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHotelDetail();
  }, []);

  useEffect(() => {
    if (hotelDetail.id) {
      fetchHotelImage(hotelDetail.id);
    }
  }, [hotelDetail]);

  const handleSearch = () => {
    const checkin = dayjs(dateCheckin.in).format("YYYY-MM-DD");
    const checkout = dayjs(dateCheckin.out).format("YYYY-MM-DD");
    localStorage.setItem(
      "dateCheckIn",
      JSON.stringify({
        checkin: checkin,
        checkout: checkout,
      })
    );
    localStorage.setItem("fromSelectDate", "true");
    Router.push(
      `/hotels/${hotel_username}/rooms?checkin=${checkin}&checkout=${checkout}`
    );
  };

  return (
    <Box>
      {error?.status_code || (!hotelDetail?.isActive && !isLoading) ? (
        <ErrorPage
          error={error?.status_code || 400}
          errorMessage={
            error?.message || "Room Feature on this hotel was disabled!"
          }
          t={t}
        />
      ) : (
        <Container maxWidth={false} className={classes.root}>
          <Navbar t={t} title="Select Date" withLogo />
          <Box className={classes.body}>
            <Box className={classes.hero}>
              <Box>
                <Typography className={classes.hotelName} variant="h1">
                  {hotelDetail.name}
                </Typography>
                <Typography>{hotelDetail.address}</Typography>
              </Box>
              <Box className={classes.hotelImageWrapper}>
                <ImageSlider listImage={hotelImages} />
              </Box>
            </Box>
            <Box className={classes.selectCheckinCheckoutWrapper}>
              <SelectCheckinCheckout
                t={t}
                hotel={hotelDetail.name}
                dateCheckin={dateCheckin}
                handleUpdateCheckin={(date) =>
                  setDateCheckin({
                    ...dateCheckin,
                    in: date,
                    out: nextDayDate(date),
                  })
                }
                handleUpdateCheckout={(date) =>
                  setDateCheckin({ ...dateCheckin, out: date })
                }
                handleSearch={handleSearch}
              />
            </Box>
          </Box>
          <Footer t={t} hotel={hotelDetail} />
          <Loading t={t} open={isLoading} onClose={() => setIsLoading(false)} />
        </Container>
      )}
    </Box>
  );
};

Rooms.getInitialProps = async () => ({
  namespacesRequired: ["common"],
});

export default withTranslation("common")(Rooms);
